// A game case on the rack: a rounded plastic box whose front is the app's
// cover, drawn from its screenshot, title and two colours.

import * as THREE from "three";
import { RoundedBoxGeometry } from "three/addons/geometries/RoundedBoxGeometry.js";
import type { AppInfo } from "../core/types.ts";
import type { SurfaceAnchor } from "../room/contract.ts";
import { LAYER_SELECT } from "./device.ts";
import { LAYER_CAST } from "./live-shadows.ts";
import { face, faceReady } from "./type.ts";

export const CASE_W = 0.2;
export const CASE_H = 0.28;
const CASE_D = 0.024;
const LEAN = THREE.MathUtils.degToRad(7);
const COVER_W = 400;
const COVER_H = 560;

function luminance(hex: string): number {
  const c = new THREE.Color(hex);
  return 0.2126 * c.r + 0.7152 * c.g + 0.0722 * c.b;
}

function loadImage(url: string): Promise<HTMLImageElement | null> {
  return new Promise((resolve) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = () => resolve(null);
    image.src = url;
  });
}

/** Break a title into at most two lines that fit `width`. */
function wrap(g: CanvasRenderingContext2D, title: string, width: number): string[] {
  const words = title.split(/\s+/);
  const lines: string[] = [];
  let line = "";
  for (const word of words) {
    const next = line ? `${line} ${word}` : word;
    if (line && g.measureText(next).width > width) {
      lines.push(line);
      line = word;
    } else {
      line = next;
    }
  }
  lines.push(line);
  return lines.length > 2 ? [lines[0]!, lines.slice(1).join(" ")] : lines;
}

/**
 * Draw a cover: the whole screenshot in a window at the top, then the title
 * and tagline on the colour field. The window takes the screenshot's own
 * shape, so nothing in it is cropped.
 */
export async function drawCover(canvas: HTMLCanvasElement, app: AppInfo): Promise<void> {
  const shot = app.shot ? await loadImage(app.shot) : null;
  const g = canvas.getContext("2d")!;
  const [field, accent] = app.colors;
  const ink = luminance(field) > 0.35 ? "#171226" : "#fcf6ff";
  g.globalAlpha = 1;
  g.fillStyle = field;
  g.fillRect(0, 0, COVER_W, COVER_H);

  const pad = 26;
  const maxW = COVER_W - pad * 2;
  const maxH = 310;
  const aspect = shot ? shot.width / shot.height : 16 / 10;
  let w = maxW;
  let h = w / aspect;
  if (h > maxH) {
    h = maxH;
    w = h * aspect;
  }
  const x = (COVER_W - w) / 2;
  const window = () => {
    g.beginPath();
    g.roundRect(x, pad, w, h, 18);
  };
  g.save();
  window();
  g.clip();
  if (shot) {
    g.imageSmoothingQuality = "high";
    g.drawImage(shot, x, pad, w, h);
  } else {
    // No screenshot yet: a big initial on the accent colour.
    g.fillStyle = accent;
    g.fillRect(x, pad, w, h);
    g.fillStyle = luminance(accent) > 0.35 ? "#171226" : "#fcf6ff";
    g.font = face(900, 150);
    g.textAlign = "center";
    g.textBaseline = "middle";
    g.fillText((app.title.trim()[0] ?? "?").toUpperCase(), COVER_W / 2, pad + h / 2 + 8);
  }
  g.restore();
  g.lineWidth = 8;
  g.strokeStyle = "#171226";
  window();
  g.stroke();

  // Title, sized down until two lines fit; the tagline takes what is left.
  const bottom = COVER_H - 30;
  g.fillStyle = ink;
  g.textAlign = "left";
  g.textBaseline = "alphabetic";
  let size = 60;
  let lines: string[] = [];
  for (; size >= 30; size -= 4) {
    g.font = face(900, size);
    lines = wrap(g, app.title, maxW);
    if (lines.every((line) => g.measureText(line).width <= maxW)) break;
  }
  let y = pad + h + 30 + size * 0.84;
  for (const line of lines) {
    g.fillText(line, pad, y);
    y += size * 1.06;
  }
  g.font = face(600, 23);
  g.globalAlpha = 0.82;
  y += 4;
  for (const line of wrap(g, app.tagline, maxW)) {
    if (y > bottom - 58) break;
    g.fillText(line, pad, y);
    y += 29;
  }
  g.font = face(700, 22);
  g.globalAlpha = 0.72;
  g.fillText(app.author, pad, bottom - 20);
  g.globalAlpha = 1;

  // The accent band along the bottom edge.
  g.fillStyle = accent;
  g.fillRect(0, bottom, COVER_W, 30);
}

export class CaseActor {
  /** World placement: the point on the ledge the case stands on, and its yaw. */
  readonly root = new THREE.Group();
  /** The case itself; moved and tilted when selected. */
  readonly body = new THREE.Group();
  readonly hitbox: THREE.Mesh;
  private canvas = document.createElement("canvas");
  private texture: THREE.CanvasTexture;
  private mesh: THREE.Mesh;

  constructor(public app: AppInfo) {
    this.root.name = `case:${app.id}`;
    // The texture's storage is allocated at first upload and cannot be
    // resized, so the canvas takes its final size before the texture exists.
    this.canvas.width = COVER_W;
    this.canvas.height = COVER_H;
    const g = this.canvas.getContext("2d")!;
    g.fillStyle = app.colors[0];
    g.fillRect(0, 0, COVER_W, COVER_H);
    this.texture = new THREE.CanvasTexture(this.canvas);
    this.texture.colorSpace = THREE.SRGBColorSpace;
    this.texture.anisotropy = 8;
    const shell = new THREE.MeshStandardMaterial({ color: app.colors[0], roughness: 0.38 });
    const spine = new THREE.MeshStandardMaterial({ color: app.colors[1], roughness: 0.38 });
    const cover = new THREE.MeshStandardMaterial({ map: this.texture, roughness: 0.42 });
    // Box material order: +X, -X, +Y, -Y, +Z (front), -Z (back).
    this.mesh = new THREE.Mesh(new RoundedBoxGeometry(CASE_W, CASE_H, CASE_D, 3, 0.007), [spine, spine, shell, shell, cover, shell]);
    this.mesh.position.y = CASE_H / 2;
    this.mesh.layers.enable(LAYER_CAST);
    this.mesh.castShadow = true;
    this.mesh.receiveShadow = true;
    this.body.add(this.mesh);
    this.body.rotation.x = -LEAN;
    this.root.add(this.body);
    this.hitbox = new THREE.Mesh(new THREE.BoxGeometry(CASE_W * 1.1, CASE_H * 1.04, CASE_D * 4), new THREE.MeshBasicMaterial());
    this.hitbox.position.set(0, CASE_H / 2, 0);
    this.hitbox.layers.set(LAYER_SELECT);
    this.hitbox.userData.case = this;
    this.root.add(this.hitbox);
    void this.redraw();
  }

  async redraw(): Promise<void> {
    await faceReady(`${this.app.title} ${this.app.tagline} ${this.app.author}`);
    await drawCover(this.canvas, this.app);
    this.texture.needsUpdate = true;
  }

  update(app: AppInfo): void {
    const changed = app.title !== this.app.title || app.shot !== this.app.shot || app.colors.join() !== this.app.colors.join();
    this.app = app;
    if (changed) void this.redraw();
  }

  place(anchor: SurfaceAnchor): void {
    this.root.position.fromArray(anchor.position);
    this.root.rotation.y = anchor.yaw;
  }

  /** 0 rests on the ledge; 1 is pulled out and upright, facing the viewer. */
  pull(t: number): void {
    this.body.position.set(0, t * 0.03, t * 0.1);
    this.body.rotation.x = -LEAN * (1 - t);
  }

  headPoint(): THREE.Vector3 {
    this.root.updateMatrixWorld(true);
    return this.root.localToWorld(new THREE.Vector3(0, CASE_H, 0));
  }

  dispose(): void {
    this.texture.dispose();
    this.mesh.geometry.dispose();
    for (const material of this.mesh.material as THREE.Material[]) material.dispose();
  }
}
